"use client";

import { Skeleton } from "@heroui/react";
import { useTranslations } from "next-intl";

interface TowerSkeletonProps {
  teamsPerRow: number;
  topRowTeamsCount: number;
  levelCount?: number;
}

/**
 * Generates the number of skeleton team cards for each level
 * Level 1: topRowTeamsCount teams
 * Level 2+: teamsPerRow teams each
 */
function getSkeletonLevelCounts(
  teamsPerRow: number,
  topRowTeamsCount: number,
): number[] {
  return [topRowTeamsCount, teamsPerRow, teamsPerRow, teamsPerRow];
}

function SkeletonTeamCard() {
  return (
    <div
      className={[
        // Base styles matching TeamCard
        "relative flex flex-shrink-0 flex-col overflow-visible",
        "rounded-lg sm:rounded-xl md:rounded-2xl border-2",
        "border-zinc-700 bg-zinc-900/60",
        // Mobile: responsive width matching TeamCard
        "w-[calc(50%-6px)] max-w-[170px] min-h-[180px]",
        // Tablet and up - larger fixed sizing
        "sm:w-[155px] sm:max-w-none sm:min-h-[240px]",
        "md:w-[180px] md:min-h-[280px]",
        "lg:w-[200px] lg:min-h-[300px]",
        "xl:w-[210px]",
        "p-2 sm:p-3 md:p-4",
      ].join(" ")}
    >
      {/* Team logo skeleton */}
      <div className="flex justify-center mb-2 sm:mb-3">
        <Skeleton className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full" />
      </div>

      {/* Team name skeleton */}
      <Skeleton className="h-4 sm:h-5 w-3/4 mx-auto rounded-md mb-2" />

      {/* Player avatars skeleton */}
      <div className="flex justify-center gap-1 sm:gap-2 mb-2 sm:mb-3">
        <Skeleton className="w-6 h-6 sm:w-8 sm:h-8 rounded-full" />
        <Skeleton className="w-6 h-6 sm:w-8 sm:h-8 rounded-full" />
      </div>

      {/* Stats skeleton */}
      <div className="flex justify-center gap-2 sm:gap-3 mt-auto">
        <Skeleton className="h-3 sm:h-4 w-8 sm:w-10 rounded" />
        <Skeleton className="h-3 sm:h-4 w-8 sm:w-10 rounded" />
      </div>
    </div>
  );
}

function SkeletonLevel({
  level,
  teamCount,
}: {
  level: number;
  teamCount: number;
}) {
  const t = useTranslations();

  const getLevelLabel = (levelIndex: number): string => {
    if (levelIndex === 0) return t("tower.levelTop");
    return t("tower.level", { level: levelIndex + 1 });
  };

  return (
    <div
      className="flex flex-col items-center gap-3 sm:gap-6"
      style={{ scrollSnapAlign: "center" }}
    >
      {/* Level label with decorative lines */}
      <div className="mb-1 sm:mb-4 flex items-center gap-2 sm:gap-3">
        <div className="h-[1px] w-[30px] sm:w-[60px] md:w-[250px] bg-gradient-to-r from-transparent to-gold-dark" />
        <span className="font-cinzel text-[0.65rem] sm:text-[0.75rem] md:text-[0.85rem] font-bold uppercase tracking-[0.08em] sm:tracking-[0.12em] text-gold whitespace-nowrap">
          {getLevelLabel(level)}
        </span>
        <div className="h-[1px] w-[30px] sm:w-[60px] md:w-[250px] bg-gradient-to-l from-transparent to-gold-dark" />
      </div>

      {/* Skeleton team cards grid */}
      <div className="flex flex-wrap justify-center gap-2.5 sm:gap-[14px] md:gap-[18px] xl:gap-[22px] w-full px-2 sm:px-0 sm:max-w-none">
        {Array.from({ length: teamCount }).map((_, index) => (
          <SkeletonTeamCard key={`skeleton-${level}-${index}`} />
        ))}
      </div>
    </div>
  );
}

function SkeletonLevelNavigation({ levelCount }: { levelCount: number }) {
  const t = useTranslations();

  return (
    <nav
      aria-label={t("tower.levelNavigation")}
      className="sticky top-0 z-10 bg-background/80 backdrop-blur-md py-2 -mx-2 px-2 sm:-mx-4 sm:px-4"
    >
      <div className="flex gap-2 justify-center items-center flex-wrap">
        {Array.from({ length: levelCount }).map((_, index) => (
          <Skeleton
            key={`skeleton-nav-${index}`}
            className="w-10 h-10 rounded-lg"
          />
        ))}
      </div>
    </nav>
  );
}

export function TowerSkeleton({
  teamsPerRow,
  topRowTeamsCount,
  levelCount = 4,
}: TowerSkeletonProps) {
  const levelCounts = getSkeletonLevelCounts(teamsPerRow, topRowTeamsCount);

  return (
    <div className="w-full flex flex-col gap-3 sm:gap-6 md:gap-8">
      {/* Level Navigation Skeleton */}
      <SkeletonLevelNavigation levelCount={levelCount} />

      {/* Tower Container */}
      <div className="pb-2 sm:pb-6 md:pb-8">
        <div className="flex flex-col items-center gap-2 sm:gap-6 md:gap-8">
          {levelCounts.map((teamCount, levelIndex) => (
            <div
              key={`skeleton-level-${levelIndex}`}
              className="w-full even:bg-black/20 sm:even:bg-black/50 py-4 sm:py-8 md:py-10 px-0 sm:px-2 rounded-xl sm:rounded-3xl lg:even:bg-transparent"
            >
              <SkeletonLevel level={levelIndex} teamCount={teamCount} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
